import React, { useState, useEffect, useRef } from 'react';
import { Icon } from '@iconify/react';
import Laxman_Niroula_Resume from '../src/Assests/Laxman_Niroula_Resume.pdf';

// Import images from the assets folder
import laxman from '../src/Assests/laxman.jpg';
import project from '../src/Assests/project.png';
import Scren from '../src/Assests/Scren.png';
import gym from '../src/Assests/gym.png';

// Main App component
const App = () => {
  // State for dark mode
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // State for mobile menu
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Refs for closing mobile menu on outside click
  const navRef = useRef(null);
  const mobileMenuRef = useRef(null);

  // Smooth scrolling function
  const scrollToSection = (event, id) => {
    event.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      if (isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    }
  };

  const toggleTheme = () => {
    setIsDarkMode(prevMode => !prevMode);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(prev => !prev);
  };

  useEffect(() => {
    const html = document.documentElement;
    if (isDarkMode) {
      html.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      html.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  // Hook to close mobile menu when clicking outside
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (isMobileMenuOpen && mobileMenuRef.current && !mobileMenuRef.current.contains(event.target) && navRef.current && !navRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isMobileMenuOpen]);

  const skills = [
    {
      name: 'HTML',
      icon: <Icon icon="devicon:html5" className="w-16 h-16 mx-auto mb-4 text-orange-500 dark:text-orange-400" />,
      color: 'border-orange-500 dark:border-orange-400'
    },
    {
      name: 'CSS',
      icon: <Icon icon="devicon:css3" className="w-16 h-16 mx-auto mb-4 text-blue-600 dark:text-blue-400" />,
      color: 'border-blue-600 dark:border-blue-400'
    },
    {
      name: 'JAVASCRIPT',
      icon: <Icon icon="devicon:javascript" className="w-16 h-16 mx-auto mb-4 text-yellow-500 dark:text-yellow-400" />,
      color: 'border-yellow-500 dark:border-yellow-400'
    },
    {
      name: 'REACT',
      icon: <Icon icon="devicon:react" className="w-16 h-16 mx-auto mb-4 text-blue-400 dark:text-blue-300" />,
      color: 'border-blue-400 dark:border-blue-300'
    },
    {
      name: 'PYTHON',
      icon: <Icon icon="devicon:python" className="w-16 h-16 mx-auto mb-4 text-yellow-600 dark:text-yellow-500" />,
      color: 'border-yellow-600 dark:border-yellow-500'
    },
    {
      name: 'GITHUB',
      icon: <Icon icon="mdi:github" className="w-16 h-16 mx-auto mb-4 text-gray-700 dark:text-gray-300" />,
      color: 'border-gray-700 dark:border-gray-300'
    },
  ];

  return (
    <div className="font-inter antialiased text-gray-800 bg-gray-50 dark:text-gray-200 dark:bg-gray-900 transition-colors duration-500">
      {/* Navigation Bar */}
      <nav ref={navRef} className="fixed w-full z-50 bg-white shadow-sm py-4 px-6 md:px-12 flex justify-between items-center rounded-b-lg dark:bg-gray-800 dark:shadow-lg transition-colors duration-500">
        <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
          <a href="#hero" onClick={(e) => scrollToSection(e, 'hero')}>Laxman Niroula</a>
        </div>
        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-8">
          <a href="#hero" onClick={(e) => scrollToSection(e, 'hero')} className="text-gray-700 hover:text-blue-600 dark:text-gray-300 dark:hover:text-blue-400 transition duration-300">Home</a>
          <a href="#about" onClick={(e) => scrollToSection(e, 'about')} className="text-gray-700 hover:text-blue-600 dark:text-gray-300 dark:hover:text-blue-400 transition duration-300">About</a>
          <a href="#skills" onClick={(e) => scrollToSection(e, 'skills')} className="text-gray-700 hover:text-blue-600 dark:text-gray-300 dark:hover:text-blue-400 transition duration-300">Skills</a>
          <a href="#projects" onClick={(e) => scrollToSection(e, 'projects')} className="text-gray-700 hover:text-blue-600 dark:text-gray-300 dark:hover:text-blue-400 transition duration-300">Projects</a>
          <a href="#contact" onClick={(e) => scrollToSection(e, 'contact')} className="text-gray-700 hover:text-blue-600 dark:text-gray-300 dark:hover:text-blue-400 transition duration-300">Contact</a>
          {/* Social Icons for Desktop */}
          <div className="flex items-center space-x-4 ml-4">
            <a href="https://www.linkedin.com/in/laxman-niroula" target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-blue-600 dark:text-gray-300 dark:hover:text-blue-400 transition duration-300">
              <Icon icon="mdi:linkedin" className="w-5 h-5" />
            </a>
            <a href="https://github.com/laxmanniroula314" target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100 transition duration-300">
              <Icon icon="mdi:github" className="w-5 h-5" />
            </a>
            <a href="mailto:laxmanniroula45@gmail.com" className="text-gray-700 hover:text-red-600 dark:text-gray-300 dark:hover:text-red-400 transition duration-300">
              <Icon icon="mdi:email" className="w-5 h-5" />
            </a>
          </div>
          {/* Theme Toggle Button for Desktop */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Toggle dark mode"
          >
            {isDarkMode ? <Icon icon="mdi:white-balance-sunny" className="w-5 h-5" /> : <Icon icon="mdi:moon-waning-crescent" className="w-5 h-5" />}
          </button>
        </div>
        {/* Mobile menu icon and Theme Toggle */}
        <div className="md:hidden flex items-center space-x-4">
          {/* Theme Toggle Button for Mobile */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Toggle dark mode"
          >
            {isDarkMode ? <Icon icon="mdi:white-balance-sunny" className="w-5 h-5" /> : <Icon icon="mdi:moon-waning-crescent" className="w-5 h-5" />}
          </button>
          <button onClick={toggleMobileMenu} className="text-gray-700 dark:text-gray-300 focus:outline-none">
            {isMobileMenuOpen ? <Icon icon="mdi:close" className="w-6 h-6" /> : <Icon icon="mdi:menu" className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div ref={mobileMenuRef} className="mobile-menu-overlay md:hidden fixed inset-0 bg-white dark:bg-gray-900 bg-opacity-95 dark:bg-opacity-95 z-40 flex flex-col items-center justify-center space-y-8 transition-opacity duration-300 ease-in-out">
          <a href="#hero" onClick={(e) => scrollToSection(e, 'hero')} className="text-2xl font-semibold text-gray-800 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 transition duration-300">Home</a>
          <a href="#about" onClick={(e) => scrollToSection(e, 'about')} className="text-2xl font-semibold text-gray-800 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 transition duration-300">About</a>
          <a href="#skills" onClick={(e) => scrollToSection(e, 'skills')} className="text-2xl font-semibold text-gray-800 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 transition duration-300">Skills</a>
          <a href="#projects" onClick={(e) => scrollToSection(e, 'projects')} className="text-2xl font-semibold text-gray-800 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 transition duration-300">Projects</a>
          <a href="#contact" onClick={(e) => scrollToSection(e, 'contact')} className="text-2xl font-semibold text-gray-800 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 transition duration-300">Contact</a>
          {/* Social Icons for Mobile within menu */}
          <div className="flex items-center space-x-6 mt-8">
            <a href="https://www.linkedin.com/in/laxman-niroula" target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-blue-600 dark:text-gray-300 dark:hover:text-blue-400 transition duration-300">
              <Icon icon="mdi:linkedin" className="w-7 h-7" />
            </a>
            <a href="https://github.com/laxmanniroula314" target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100 transition duration-300">
              <Icon icon="mdi:github" className="w-7 h-7" />
            </a>
            <a href="mailto:laxmanniroula45@gmail.com" className="text-gray-700 hover:text-red-600 dark:text-gray-300 dark:hover:text-red-400 transition duration-300">
              <Icon icon="mdi:email" className="w-7 h-7" />
            </a>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section id="hero" className="relative h-screen flex items-center justify-center text-center bg-gradient-to-br from-blue-300 to-indigo-100 dark:from-gray-800 dark:to-gray-950 px-4 pt-16 transition-colors duration-500">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-gray-900 dark:text-white leading-tight mb-4 animate-fade-in-up">
            Hi.. I'm <span className="text-blue-600 dark:text-blue-400">Laxman Niroula</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-700 dark:text-gray-300 mb-8 max-w-2xl mx-auto animate-fade-in-up delay-200">
            Front-End Develope from Nepal, experienced in designing and delivering scalable, responsive, user-focused web applications with clean and maintainable code.
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4 animate-fade-in-up delay-400">
            <a
              href="#projects"
              onClick={(e) => scrollToSection(e, 'projects')}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-full shadow-lg transition duration-300 ease-in-out transform hover:scale-105 dark:bg-blue-700 dark:hover:bg-blue-800"
            >
              View My Work
            </a>
            <a
              href={Laxman_Niroula_Resume}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center bg-white border border-blue-600 text-blue-600 hover:bg-blue-50 font-semibold py-3 px-8 rounded-full shadow-lg transition duration-300 ease-in-out transform hover:scale-105 dark:bg-gray-800 dark:border-blue-400 dark:text-blue-400 dark:hover:bg-gray-700"
            >
              View My CV
            </a>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
          <Icon icon="mdi:chevron-down" className="w-8 h-8 text-gray-500 dark:text-gray-400 animate-bounce" />
        </div>
      </section>

      {/* About Me Section */}
      <section id="about" className="py-1 md:py-2 px-4 bg-white dark:bg-gray-900 transition-colors duration-500">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center md:space-x-12">
          <div className="md:w-1/3 mb-8 md:mb-0 flex justify-center">
            <img
              src={laxman}
              alt="Laxman Niroula"
               className="rounded-full shadow-lg w-100 h-90 sm:w-90 sm:h-90 object-cover border-4 border-blue-400"
              onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/300x300/E0E7FF/4F46E5?text=Your+Photo"; }}
            />
          </div>
          <div className="md:w-2/3 text-center md:text-left">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-6">About <span className="text-blue-600 dark:text-blue-400">Me</span></h2>
            <p className="text-lg text-gray-800 dark:text-gray-300 leading-relaxed mb-4">
              Hello! I'm Laxman Niroula, a Front-End Developer from eastern part of Nepal, Biratnagar. I graduated with a BSc (Hons) in Computer Systems Engineering from ISMT College, affiliated with the University of Sunderland. I'm passionate about creating user-friendly and impactful web applications that combine creativity, efficiency, and robust performance.
           </p>
           <p className="text-lg text-gray-800 dark:text-gray-300 leading-relaxed mb-4">
             I enjoy tackling challenges and take a hands-on approach throughout the development process. With experience in both front-end and back-end technologies, my focus is on crafting applications that are seamless, user-friendly, and high-performing. I believe in a holistic approach to development, from concept to deployment.
           </p>
           <p className="text-lg text-gray-800 dark:text-gray-300 leading-relaxed mb-4">
             When I'm not coding, I enjoy reading novels, writing poetry, traveling, playing games, and watching films. These interests fuel my creativity and inspire me to build innovative web projects and solutions. I'm always eager to learn and grow, both professionally and personally.
           </p>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="py-8 md:py-15 px-3 bg-gray-150 dark:bg-gray-980 transition-colors duration-500">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">Skills & <span className="text-blue-600 dark:text-blue-400">Abilities</span></h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 mb-12">
            A showcase of my core competencies and the expertise behind my work.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {skills.map((skill, index) => (
              <div
                key={index}
                className={`relative bg-gray-100 dark:bg-gray-800 p-6 rounded-xl shadow-md border-2 ${skill.color}
                  flex flex-col items-center justify-center text-center
                  transform transition-all duration-300 hover:scale-105 hover:shadow-lg`}
              >
                {skill.icon}
                <h3 className="text-xl font-semibold text-gray-800 dark:text-white">{skill.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-16 md:py-24 px-4 bg-white dark:bg-gray-900 transition-colors duration-500">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-12">My <span className="text-blue-600 dark:text-blue-400">Projects</span></h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 mb-12">
            A collection of Projects that showcase my skills, creativity, and passion.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Project Card 1 */}
            <div className="bg-gray-50 rounded-xl shadow-md overflow-hidden hover:shadow-xl transition duration-300 dark:bg-gray-800 dark:shadow-xl">
              <img
                src={gym}
                alt="gym  Screenshot"
                className="w-full h-48 object-cover"
                onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/600x400/D1FAE5/065F46?text=Project+1+Screenshot"; }}
              />
              <div className="p-6">
                <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2"> Gym Website</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4 text-sm">
                  Gym Center is a modern React-based gym app with membership management, product catalog, shopping cart, and seamless payment integration.
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full dark:bg-blue-900 dark:text-blue-200">React</span>
                  <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full dark:bg-green-900 dark:text-green-200">Node.js</span>
                  <span className="bg-purple-100 text-purple-800 text-xs font-medium px-2.5 py-0.5 rounded-full dark:bg-purple-900 dark:text-purple-200">MongoDB</span>
                  <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2.5 py-0.5 rounded-full dark:bg-yellow-900 dark:text-yellow-200">Tailwind CSS</span>
                </div>
                <div className="flex justify-center space-x-4">
                  <a
                    href="https://gym-center-flax.vercel.app/" target="_blank" rel="noopener noreferrer"
                    className="flex items-center text-blue-600 hover:text-blue-800 transition duration-300 dark:text-blue-400 dark:hover:text-blue-200"
                  >
                    <Icon icon="mdi:link" className="w-5 h-5 mr-1" /> Demo
                  </a>
                  <a
                    href="https://github.com/laxmanniroula314/gym-center/tree/main/gym" target="_blank" rel="noopener noreferrer"
                    className="flex items-center text-gray-700 hover:text-gray-900 transition duration-300 dark:text-gray-300 dark:hover:text-gray-100"
                  >
                    <Icon icon="mdi:github" className="w-5 h-5 mr-1" /> Code
                  </a>
                </div>
              </div>
            </div>

            {/* Project Card 2 */}
            <div className="bg-gray-50 rounded-xl shadow-md overflow-hidden hover:shadow-xl transition duration-300 dark:bg-gray-800 dark:shadow-xl">
              <img
                src={project}
                alt="Task Management App Screenshot"
                className="w-full h-48 object-cover"
                onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/600x400/FFE4E6/E11D48?text=Project+2+Screenshot"; }}
              />
              <div className="p-6">
                <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">Task Management App</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4 text-sm">
                  A collaborative task management application with real-time updates, drag-and-drop functionality, and user roles.
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="bg-red-100 text-red-800 text-xs font-medium px-2.5 py-0.5 rounded-full dark:bg-red-900 dark:text-red-200">Vue.js</span>
                  <span className="bg-indigo-100 text-indigo-800 text-xs font-medium px-2.5 py-0.5 rounded-full dark:bg-indigo-900 dark:text-indigo-200">Firebase</span>
                  <span className="bg-teal-100 text-teal-800 text-xs font-medium px-2.5 py-0.5 rounded-full dark:bg-teal-900 dark:text-teal-200">Tailwind CSS</span>
                </div>
                <div className="flex justify-center space-x-4">
                  <a
                    href="https://your-project2-demo.com" target="_blank" rel="noopener noreferrer"
                    className="flex items-center text-blue-600 hover:text-blue-800 transition duration-300 dark:text-blue-400 dark:hover:text-blue-200"
                  >
                    <Icon icon="mdi:link" className="w-5 h-5 mr-1" /> Demo
                  </a>
                  <a
                    href="https://github.com/yourusername/project2" target="_blank" rel="noopener noreferrer"
                    className="flex items-center text-gray-700 hover:text-gray-900 transition duration-300 dark:text-gray-300 dark:hover:text-gray-100"
                  >
                    <Icon icon="mdi:github" className="w-5 h-5 mr-1" /> Code
                  </a>
                </div>
              </div>
            </div>

            {/* Project Card 3 */}
            <div className="bg-gray-50 rounded-xl shadow-md overflow-hidden hover:shadow-xl transition duration-300 dark:bg-gray-800 dark:shadow-xl">
              <img
                src={Screen}
                alt="Personal Blog/CMS Screenshot"
                className="w-full h-48 object-cover"
                onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/600x400/E0F2FE/1D4ED8?text=Project+3+Screenshot"; }}
              />
              <div className="p-6">
                <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">Personal Blog/CMS</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4 text-sm">
                  A custom content management system for managing blog posts, categories, and user comments with a clean front-end interface.
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2.5 py-0.5 rounded-full dark:bg-yellow-900 dark:text-yellow-200">Python</span>
                  <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full dark:bg-blue-900 dark:text-blue-200">Django</span>
                  <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full dark:bg-green-900 dark:text-green-200">PostgreSQL</span>
                </div>
                <div className="flex justify-center space-x-4">
                  <a
                    href="https://your-project3-demo.com" target="_blank" rel="noopener noreferrer"
                    className="flex items-center text-blue-600 hover:text-blue-800 transition duration-300 dark:text-blue-400 dark:hover:text-blue-200"
                  >
                    <Icon icon="mdi:link" className="w-5 h-5 mr-1" /> Demo
                  </a>
                  <a
                    href="https://github.com/yourusername/project3" target="_blank" rel="noopener noreferrer"
                    className="flex items-center text-gray-700 hover:text-gray-900 transition duration-300 dark:text-gray-300 dark:hover:text-gray-100"
                  >
                    <Icon icon="mdi:github" className="w-5 h-5 mr-1" /> Code
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-8 md:py-15 px-4 bg-gray-100 dark:bg-gray-950 transition-colors duration-500">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Connect <span className="text-blue-600 dark:text-blue-400">With Me</span>
          </h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-8">
            My inbox is always open - say hi anytime!
          </p>
          <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg">
            <form
              action="https://api.web3forms.com/submit"
              method="POST"
              className="space-y-6"
            >
              <input type="hidden"
                name="access_key"
                value="3e449f5e-7a52-445d-9cd8-2878ff93428a"
              />
              <div className="form-group text-left">
                <label htmlFor="name" className="block text-gray-700 dark:text-gray-300 text-sm font-semibold mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  id="name"
                  name="name"
                  placeholder="Enter your name"
                  required
                />
              </div>

              <div className="form-group text-left">
                <label htmlFor="email" className="block text-gray-700 dark:text-gray-300 text-sm font-semibold mb-2">
                  Email
                </label>
                <input
                  type="email"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  id="email"
                  name="email"
                  placeholder="Enter your email"
                  required
                />
              </div>

              <div className="form-group text-left">
                <label htmlFor="message" className="block text-gray-700 dark:text-gray-300 text-sm font-semibold mb-2">
                  Message
                </label>
                <textarea
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white resize-y"
                  id="message"
                  name="message"
                  rows="3"
                  placeholder="Enter your message"
                  required
                ></textarea>
              </div>

              <button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-md shadow-md dark:bg-blue-700 dark:hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
              >
                Submit Message
              </button>
            </form>


              {/* <div className="flex justify-center space-x-6 mt-8">
            <a
              href="https://www.linkedin.com/in/laxman-niroula" target="_blank" rel="noopener noreferrer"
              className="text-gray-700 hover:text-blue-600 transition duration-300 dark:text-gray-300 dark:hover:text-blue-400"
            >
              <Linkedin className="w-8 h-8" />
            </a>
            <a
              href="https://github.com/laxmanniroula314" target="_blank" rel="noopener noreferrer"
              className="text-gray-700 hover:text-gray-900 transition duration-300 dark:text-gray-300 dark:hover:text-gray-100"
            >
              <Github className="w-8 h-8" />
            </a>
            <a
              href="mailto:laxmanniroula45@gmail.com"
              className="text-gray-700 hover:text-red-600 transition duration-300 dark:text-gray-300 dark:hover:text-red-400"
            >
              <Mail className="w-8 h-8" />
            </a>
          </div>
           */}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-gray-800 text-white text-center text-sm rounded-t-lg dark:bg-gray-900 transition-colors duration-500">
        <p>&copy; {new Date().getFullYear()} All rights reserved.</p>
        <p className="mt-2">Built by Laxman.</p>
      </footer>
    </div>
  );
};

export default App;